import { Component } from '@theme/component';
import { morphSection, buildSectionSelector } from '@theme/section-renderer';

/** Minimum time the loading skeleton stays visible before morphing in results. */
const MIN_SKELETON_MS = 450;

/** Delay before starting fetch so the skeleton can paint. */
const SKELETON_PAINT_DELAY_MS = 120;

/**
 * Lazy-loads Search & Discovery recommendations for product-list-promotion-slider sections.
 */
class ProductListPromotionSliderPad extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #isFetching = false;

  /** @type {number} */
  #skeletonShownAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.fetchWithoutSkeleton === 'true') {
      this.#hideSection();
    } else if (this.querySelector('card-product-component[data-product-id]') && !this.#meetsMinimumProductCount(this)) {
      this.#hideSection();
      this.replaceChildren();
    }

    if (this.dataset.loading === 'true') {
      this.#skeletonShownAt = performance.now();
    }

    this.#scheduleRecommendationsLoad();
  }

  #scheduleRecommendationsLoad() {
    if (this.dataset.needsFetch !== 'true' || this.dataset.recommendationsPerformed === 'true') {
      return;
    }

    const eagerFetch =
      this.dataset.loading === 'true' ||
      this.dataset.fetchWithoutSkeleton === 'true' ||
      this.dataset.padInBackground === 'true';
    const delay = this.dataset.loading === 'true' ? SKELETON_PAINT_DELAY_MS : 0;

    window.setTimeout(() => {
      requestAnimationFrame(() => {
        if (eagerFetch) {
          this.#intersectionObserver.disconnect();
          this.#loadRecommendations();
          return;
        }

        this.#loadRecommendationsIfNearViewport();
      });
    }, delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #loadRecommendationsIfNearViewport() {
    if (this.dataset.needsFetch !== 'true' || this.dataset.recommendationsPerformed === 'true') {
      return;
    }

    const rect = this.getBoundingClientRect();
    const isNearViewport = rect.top < window.innerHeight + 400;

    if (!isNearViewport) return;

    this.#intersectionObserver.disconnect();
    this.#loadRecommendations();
  }

  async #waitForMinimumSkeletonTime() {
    if (this.#skeletonShownAt === 0) return;

    const elapsed = performance.now() - this.#skeletonShownAt;
    const remaining = MIN_SKELETON_MS - elapsed;

    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
  }

  #loadRecommendations() {
    const { productId, sectionId, intent, needsFetch, recommendationsPerformed } = this.dataset;

    if (needsFetch !== 'true' || recommendationsPerformed === 'true' || this.#isFetching) {
      return;
    }

    if (!productId || !sectionId) {
      this.#hideSection();
      return;
    }

    this.#isFetching = true;

    this.#fetchRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#hideSection();
          }
          return;
        }

        if (!result.data?.trim().length) {
          if (!Shopify.designMode) {
            this.#hideSection();
          }
          return;
        }

        const fragment = new DOMParser().parseFromString(result.data, 'text/html');
        const responsePad = fragment.querySelector('product-list-promotion-slider-pad[data-has-products="true"]');

        if (!responsePad || !this.#meetsMinimumProductCount(responsePad)) {
          if (!Shopify.designMode) {
            this.#hideSection();
            this.replaceChildren();
          }
          return;
        }

        this.#showSection();
        this.removeAttribute('hidden');

        await this.#waitForMinimumSkeletonTime();

        try {
          await morphSection(sectionId, result.data, { mode: 'full' });
        } catch (error) {
          console.error('Product list promotion slider morph error:', error);
          if (!Shopify.designMode) {
            this.#hideSection();
          }
          return;
        }

        const liveSection = document.getElementById(buildSectionSelector(sectionId));
        const livePad = liveSection?.querySelector('product-list-promotion-slider-pad[data-has-products="true"]');

        if (!livePad || !this.#meetsMinimumProductCount(livePad)) {
          if (!Shopify.designMode) {
            this.#hideSection();
            this.replaceChildren();
          }
          return;
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Product list promotion slider recommendations error:', error.message);
          if (!Shopify.designMode) {
            this.#hideSection();
          }
        }
      })
      .finally(() => {
        this.#isFetching = false;
      });
  }

  /**
   * @param {string} productId
   * @param {string} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #hideSection() {
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) {
      section.hidden = true;
    }
  }

  #showSection() {
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) {
      section.hidden = false;
      section.style.removeProperty('display');
    }
  }

  /**
   * @param {Element} pad
   * @returns {boolean}
   */
  #meetsMinimumProductCount(pad) {
    const minimumProducts = Number(pad.getAttribute('data-minimum-products') ?? this.dataset.minimumProducts) || 1;
    const productCount = this.#getUniqueProductCardCount(pad);

    if (productCount === 0) {
      return false;
    }

    return productCount >= minimumProducts;
  }

  /**
   * @param {Element} pad
   * @returns {number}
   */
  #getUniqueProductCardCount(pad) {
    const productIds = new Set();

    for (const card of pad.querySelectorAll('card-product-component[data-product-id]')) {
      const productId = card.getAttribute('data-product-id');
      if (productId) {
        productIds.add(productId);
      }
    }

    return productIds.size;
  }
}

if (!customElements.get('product-list-promotion-slider-pad')) {
  customElements.define('product-list-promotion-slider-pad', ProductListPromotionSliderPad);
}
